import React, { Component } from "react";
import PropTypes from "prop-types";

import { isDefined, noop } from "../../utils";
import { getXValue } from "../../utils/ChartDataUtil";
import { saveNodeType, isHover } from "../utils";

import { getNewXY } from "./EachTrendLine";
import StraightLine from "../components/StraightLine";
import ClickableCircle from "../components/ClickableCircle";
import HoverTextNearMouse from "../components/HoverTextNearMouse";
import GenericChartComponent from "../../GenericChartComponent";
import { getMouseCanvas } from "../../GenericComponent";

class EachTimeCycle extends Component {
  constructor(props) {
    super(props);

    this.handlePoint1Drag = this.handlePoint1Drag.bind(this);
    this.handlePoint2Drag = this.handlePoint2Drag.bind(this);
    this.handleLineDragStart = this.handleLineDragStart.bind(this);
    this.handleLineMove = this.handleLineMove.bind(this);
    this.handleHover = this.handleHover.bind(this);

    this.isHover = isHover.bind(this);
    this.saveNodeType = saveNodeType.bind(this);
    this.nodes = {};

    this.state = {
      hover: false,
    };
  }

  handleHover(moreProps) {
    if (this.state.hover !== moreProps.hovering) {
      this.setState({
        hover: moreProps.hovering,
      });
    }
  }

  handleLineDragStart() {
    const { x1, y1, x2, y2 } = this.props;

    this.dragStart = {
      x1,
      y1,
      x2,
      y2,
    };
  }

  handleLineMove(moreProps) {
    const { index, onDrag } = this.props;

    const {
      x1: x1Value,
      y1: y1Value,
      x2: x2Value,
      y2: y2Value,
    } = this.dragStart;

    const {
      xScale,
      chartConfig: { yScale },
      xAccessor,
      fullData,
    } = moreProps;
    const { startPos, mouseXY } = moreProps;

    const x1 = xScale(x1Value);
    const y1 = yScale(y1Value);
    const x2 = xScale(x2Value);
    const y2 = yScale(y2Value);

    const dx = startPos[0] - mouseXY[0];
    const dy = startPos[1] - mouseXY[1];

    const newX1Value = getXValue(
      xScale,
      xAccessor,
      [x1 - dx, y1 - dy],
      fullData,
    );
    const newY1Value = yScale.invert(y1 - dy);
    const newX2Value = getXValue(
      xScale,
      xAccessor,
      [x2 - dx, y2 - dy],
      fullData,
    );
    const newY2Value = yScale.invert(y2 - dy);

    onDrag(index, {
      x1: newX1Value,
      y1: newY1Value,
      x2: newX2Value,
      y2: newY2Value,
    });
  }

  handlePoint1Drag(moreProps) {
    const { index, onDrag } = this.props;
    const { x2, y2 } = this.props;

    const [x1Value, y1Value] = getNewXY(moreProps);

    onDrag(index, {
      x1: x1Value,
      y1: y1Value,
      x2: x2,
      y2: y2,
    });
  }

  handlePoint2Drag(moreProps) {
    const { index, onDrag } = this.props;
    const { x1, y1 } = this.props;

    const [x2Value, y2Value] = getNewXY(moreProps);

    onDrag(index, {
      x1: x1,
      y1: y1,
      x2: x2Value,
      y2: y2Value,
    });
  }

  render() {
    const { x1, y1, x2, y2, appearance, hoverText, selected, onDragComplete } =
      this.props;
    const { hover } = this.state;

    if (x2 === null || y2 === null) {
      return null;
    }

    const { stroke, fill, font, printLevels } = appearance;

    const strokeColor = stroke?.color || stroke || "#2196F3";
    const strokeWidth = stroke?.width || 1;
    const strokeOpacity = stroke?.opacity !== undefined ? stroke.opacity : 1;
    const strokeDasharray = stroke?.dash === "Solid" ? "none" : "5,5";

    const fillColor = fill?.color || fill || "#2196F3";
    const fillOpacity = fill?.opacity !== undefined ? fill.opacity : 0.05;

    const fontFamily =
      font?.fontFamily || "Helvetica Neue, Helvetica, Arial, sans-serif";
    const fontSize = font?.fontSize || 11;
    const fontColor = font?.color || "#2196F3";

    const hoverTextEnabled = hoverText?.enable;
    const hoverTextUnselected = hoverText?.text;

    const edgeColors = {
      stroke: selected || hover ? strokeColor : "#AAAAAA",
      fill: selected || hover ? strokeColor : "#FFFFFF",
    };

    const actualEdgeFill = selected || hover ? edgeColors.fill : "transparent";

    const circleRadius = selected || hover ? 6 : 0;

    return (
      <g>
        {/* Base trend line connecting p1 and p2 */}
        <StraightLine
          ref={this.saveNodeType("line")}
          selected={selected || hover}
          onHover={this.handleHover}
          onUnHover={this.handleHover}
          x1Value={x1}
          y1Value={y1}
          x2Value={x2}
          y2Value={y2}
          type="XLINE"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeOpacity={strokeOpacity}
          strokeDasharray={strokeDasharray}
          onDragStart={this.handleLineDragStart}
          onDrag={this.handleLineMove}
          onDragComplete={onDragComplete}
        />

        {/* Vertical lines and fill zones - rendered via TimeCycleComponent */}
        <TimeCycleComponent
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeOpacity={strokeOpacity}
          strokeDasharray={strokeDasharray}
          fillColor={fillColor}
          fillOpacity={fillOpacity}
          fontFamily={fontFamily}
          fontSize={fontSize}
          fontColor={fontColor}
          printLevels={printLevels !== undefined ? printLevels : true}
        />

        {/* Drag handles for endpoints */}
        {(selected || hover) && (
          <g>
            <ClickableCircle
              ref={this.saveNodeType("point1")}
              show={true}
              cx={x1}
              cy={y1}
              r={circleRadius}
              fill={actualEdgeFill}
              stroke={edgeColors.stroke}
              strokeWidth={2}
              strokeOpacity={1}
              interactiveCursorClass="react-stockcharts-move-cursor"
              onDrag={this.handlePoint1Drag}
              onDragComplete={onDragComplete}
            />
            <ClickableCircle
              ref={this.saveNodeType("point2")}
              show={true}
              cx={x2}
              cy={y2}
              r={circleRadius}
              fill={actualEdgeFill}
              stroke={edgeColors.stroke}
              strokeWidth={2}
              strokeOpacity={1}
              interactiveCursorClass="react-stockcharts-move-cursor"
              onDrag={this.handlePoint2Drag}
              onDragComplete={onDragComplete}
            />
          </g>
        )}

        {/* Hover tooltip */}
        <HoverTextNearMouse
          show={hoverTextEnabled && hover && !selected}
          text={hoverTextUnselected}
        />
      </g>
    );
  }
}

// TimeCycleComponent - Renders vertical lines, fills, and labels
class TimeCycleComponent extends Component {
  constructor(props) {
    super(props);

    this.renderSVG = this.renderSVG.bind(this);
    this.isHover = this.isHover.bind(this);
  }

  isHover(moreProps) {
    // This component doesn't need hover - parent handles it
    return false;
  }

  helper(props, moreProps) {
    const { x1, y1, x2, y2 } = props;
    const { xScale, chartConfig } = moreProps;
    const { yScale } = chartConfig;

    // Convert data coords to pixel coords
    const px1 = xScale(x1);
    const py1 = yScale(y1);
    const px2 = xScale(x2);
    const py2 = yScale(y2);

    // Calculate interval in bars
    const interval = x2 - x1;

    // Panel boundaries
    const panelTop = chartConfig.origin[1];
    const panelBottom = chartConfig.origin[1] + chartConfig.height;
    const panelLeft = chartConfig.origin[0];
    const panelRight = chartConfig.origin[0] + chartConfig.width;

    return {
      px1,
      py1,
      px2,
      py2,
      interval,
      panelTop,
      panelBottom,
      panelLeft,
      panelRight,
      xScale,
    };
  }

  renderSVG(moreProps) {
    const {
      stroke,
      strokeWidth,
      strokeOpacity,
      strokeDasharray,
      fillColor,
      fillOpacity,
      fontFamily,
      fontSize,
      fontColor,
      printLevels,
    } = this.props;

    const {
      px1,
      py1,
      px2,
      py2,
      interval,
      panelTop,
      panelBottom,
      panelLeft,
      panelRight,
      xScale,
    } = this.helper(this.props, moreProps);

    // Calculate cycle positions
    const cycles = [];
    let count = 0;
    let x;

    // Determine direction (left to right or right to left)
    const direction = px2 > px1 ? 1 : -1;

    // Generate cycle positions
    do {
      x = xScale(this.props.x1 + count * interval);
      count++;

      // Stop if we've gone past the panel boundaries
      if (direction > 0 && x > panelRight) break;
      if (direction < 0 && x < panelLeft) break;

      // Only add if within panel
      if (x >= panelLeft && x <= panelRight) {
        cycles.push({ x, count });
      }

      // Safety limit
      if (count > 100) break;
    } while (Math.abs(interval) > 0);

    const labelHeight = printLevels ? 20 : 0;
    const farY = panelBottom - labelHeight;

    return (
      <g>
        {/* Alternating fill zones */}
        {cycles.map((cycle, i) => {
          if (i === 0) return null; // Skip first zone (before first line)

          // Only fill every other zone
          if (i % 2 === 1) {
            const prevX = cycles[i - 1].x;
            const currX = cycle.x;

            return (
              <rect
                key={`fill-${i}`}
                x={Math.min(prevX, currX)}
                y={panelTop}
                width={Math.abs(currX - prevX)}
                height={farY - panelTop}
                fill={fillColor}
                opacity={fillOpacity}
              />
            );
          }
          return null;
        })}

        {/* Vertical lines */}
        {cycles.map((cycle, i) => (
          <line
            key={`line-${i}`}
            x1={cycle.x}
            y1={panelTop}
            x2={cycle.x}
            y2={farY}
            stroke={stroke}
            strokeWidth={strokeWidth}
            strokeOpacity={strokeOpacity}
            strokeDasharray={strokeDasharray}
          />
        ))}

        {/* Cycle counter labels */}
        {printLevels &&
          cycles.map((cycle, i) => (
            <text
              key={`label-${i}`}
              x={cycle.x}
              y={panelBottom - 5}
              fill={fontColor}
              fontSize={fontSize}
              fontFamily={fontFamily}
              textAnchor="middle"
              dominantBaseline="middle"
            >
              {cycle.count}
            </text>
          ))}
      </g>
    );
  }

  render() {
    return (
      <GenericChartComponent
        svgDraw={this.renderSVG}
        drawOn={["pan", "mousemove", "drag", "xAxisZoom"]}
      />
    );
  }
}

TimeCycleComponent.propTypes = {
  x1: PropTypes.number.isRequired,
  y1: PropTypes.number.isRequired,
  x2: PropTypes.number.isRequired,
  y2: PropTypes.number.isRequired,

  stroke: PropTypes.string.isRequired,
  strokeWidth: PropTypes.number.isRequired,
  strokeOpacity: PropTypes.number.isRequired,
  strokeDasharray: PropTypes.string,

  fillColor: PropTypes.string.isRequired,
  fillOpacity: PropTypes.number.isRequired,

  fontFamily: PropTypes.string.isRequired,
  fontSize: PropTypes.number.isRequired,
  fontColor: PropTypes.string.isRequired,

  printLevels: PropTypes.bool,
};

EachTimeCycle.propTypes = {
  index: PropTypes.number,

  x1: PropTypes.number.isRequired,
  y1: PropTypes.number.isRequired,
  x2: PropTypes.number,
  y2: PropTypes.number,

  appearance: PropTypes.object.isRequired,
  hoverText: PropTypes.object.isRequired,

  selected: PropTypes.bool,
  hovering: PropTypes.bool,

  onDrag: PropTypes.func.isRequired,
  onDragComplete: PropTypes.func.isRequired,
};

EachTimeCycle.defaultProps = {
  onDrag: noop,
  onDragComplete: noop,

  selected: false,
  hovering: false,

  hoverText: {
    enable: false,
  },
};

export default EachTimeCycle;
